/*
 * @Author: zhushitao
 * @Date: 2021-08-23 11:35:41
 * @LastEditors: zhushitao
 * @LastEditTime: 2021-11-17 18:52:06
 * @Description:
 */

import { isEqual } from 'lodash-es';
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';

import { CombineMessageType, AppContextType } from '@byted-cg/linkchat-plugin';
import { Avatar, Dialog } from '@byted-cg/linkchat-ui';

import classes from '../constants/classes';
import { prefixCls } from '../utils/style';
import LazyComponent from './LazyComponent';
import { IAvatarsConfig, IComponent } from './type';
import { Icon } from '../components/Icon';
import { I18n } from '@byted-cg/linkchat-plugin';
import MessageRecall from '../../../../src/pages/chat/components/MessageRecall';

interface IMessageContainerOptions {
  appContext?: AppContextType;
  message: CombineMessageType;
  avatarsConfig?: IAvatarsConfig;
  style?: React.CSSProperties;
}
const NORMAL_COLOR = '#333';
const ACTIVE_COLOR = 'rgba(47, 136, 255, 1)';
const MESSAGE_SYSTEM_TYPE = {
  MESSAGE_TYPE_WELCOME: 40001,
  MESSAGE_TYPE_END: 40002,
  MESSAGE_TYPE_TRANSFERING: 40004,
  MESSAGE_TYPE_TRANSFERED: 40003,
  CREATE_CONVERSATION_TYPE: 40005,
  SYSTEM_TIME_TYPE: 40007,
  // Display virtual agent job number message
  VIRTUAL_AGENT_ID: 40009,
};

const ReplyIcon: React.FC<{ fill?: string }> = ({ fill }) => {
  !fill && (fill = '#333333');
  return (
    <svg
      width="16"
      height="14"
      viewBox="0 0 16 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.3335 11L4.00016 13V11H2.00016C1.63197 11 1.3335 10.7015 1.3335 10.3333V1.66667C1.3335 1.29848 1.63197 1 2.00016 1H14.0002C14.3684 1 14.6668 1.29848 14.6668 1.66667V10.3333C14.6668 10.7015 14.3684 11 14.0002 11H7.3335Z"
        fill={fill}
        stroke={fill}
        stroke-width="1.33333"
        stroke-linejoin="round"
      />
      <path
        d="M4.6665 5.5V6.5"
        stroke="white"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M8 5.5V6.5"
        stroke="white"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M11.3335 5.5V6.5"
        stroke="white"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

const DeleteIcon: React.FC<{ fill?: string }> = ({ fill }) => {
  !fill && (fill = '#333333');
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M4.73607 0.370822C4.84854 0.143704 5.08006 0 5.3335 0H8.66683C8.9222 0 9.15513 0.145876 9.26661 0.375625L10.2166 2.33333H13.0002C13.3684 2.33333 13.6668 2.63181 13.6668 3C13.6668 3.36819 13.3684 3.66667 13.0002 3.66667H12.0002V13.3333C12.0002 13.7015 11.7017 14 11.3335 14H2.66683C2.29864 14 2.00016 13.7015 2.00016 13.3333V3.66667H1.00016C0.631973 3.66667 0.333496 3.36819 0.333496 3C0.333496 2.63181 0.631973 2.33333 1.00016 2.33333H3.76424L4.73607 0.370822ZM5.2521 2.33333H8.73456L8.24932 1.33333H5.74729L5.2521 2.33333ZM6.3335 6C6.3335 5.63181 6.03502 5.33333 5.66683 5.33333C5.29864 5.33333 5.00016 5.63181 5.00016 6V10C5.00016 10.3682 5.29864 10.6667 5.66683 10.6667C6.03502 10.6667 6.3335 10.3682 6.3335 10V6ZM9.00016 6C9.00016 5.63181 8.70169 5.33333 8.3335 5.33333C7.96531 5.33333 7.66683 5.63181 7.66683 6V10C7.66683 10.3682 7.96531 10.6667 8.3335 10.6667C8.70169 10.6667 9.00016 10.3682 9.00016 10V6Z"
        fill={fill}
      />
    </svg>
  );
};

const MessageContainer: React.FC<IMessageContainerOptions> = ({
  appContext,
  message,
  avatarsConfig,
  style,
}) => {
  let timer: number | undefined = undefined;
  const [showOption, setShowOption] = useState(false);
  const [showDeleteTip, setShowDeleteTip] = useState(false);
  const [showReplyTip, setShowReplyTip] = useState(false);
  const [pcDeleteColor, setPcDeleteColor] = useState(NORMAL_COLOR);
  const [pcReplyColor, setPcReplyColor] = useState(NORMAL_COLOR);
  const [popDirection, setPopDirection] = useState(false);
  const [noAvatarCls, setNoAvatarCls] = useState('');
  const MessageAvatar = useMemo(() => {
    const { avatarType = 'key', avatar } = message.user || {};
    if (!avatar || appContext?.renderConfig?.hideAvatar) return null;

    switch (avatarType) {
      case 'key':
        return avatarsConfig && avatarsConfig[avatar as string] ? (
          <Avatar
            src={avatarsConfig[avatar as string] as string}
            className="avatarSpan"
          />
        ) : null;
      case 'url':
        return <Avatar src={avatar as string} className="avatarSpan" />;
      default:
        return (
          <LazyComponent
            className="avatarSpan"
            message={message}
            config={avatar as IComponent}
          />
        );
    }
  }, [JSON.stringify(message.user), avatarsConfig]);
  // 清空所有状态
  const clearBtnStatus = () => {
    setPcDeleteColor(NORMAL_COLOR);
    setShowDeleteTip(false);
    setPcReplyColor(NORMAL_COLOR);
    setShowReplyTip(false);
  };
  const handleMessageHover = useCallback(() => {
    if (
      !(
        appContext?.renderConfig?.messageDelete ||
        (appContext?.ext?.userDefaultConfig?.openReply &&
          !appContext?.renderConfig?.hideReply)
      ) ||
      showOption
    ) {
      return;
    }
    clearBtnStatus();
    if (appContext.query?.isPc) {
      setShowOption(true);
    }
  }, [showOption]);
  const handleBtnHover = useCallback((type: string) => {
    window.clearTimeout(timer);
    if (type === 'delete') {
      setPcDeleteColor(ACTIVE_COLOR);
      setShowDeleteTip(true);
    } else {
      setPcReplyColor(ACTIVE_COLOR);
      setShowReplyTip(true);
    }
  }, []);
  const handleBtnLeave = useCallback(() => {
    setShowDeleteTip(false);
    setPcDeleteColor(NORMAL_COLOR);
    setShowReplyTip(false);
    setPcReplyColor(NORMAL_COLOR);
  }, []);
  const handleMessageLeave = useCallback(() => {
    if (
      !(
        appContext?.renderConfig?.messageDelete ||
        (appContext?.ext?.userDefaultConfig?.openReply &&
          !appContext?.renderConfig?.hideReply)
      )
    ) {
      return;
    }
    if (appContext.query?.isPc) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        setShowOption(false);
      }, 200);
    }
  }, [showOption]);
  // Long press event - start
  const timeout = useRef<any>();
  const delay = 600;
  const $track = useCallback((val: string) => {
    if (!appContext?.query?.isPc) {
      try {
        appContext?.logger?.info(val, {
          dialogId: appContext?.dialogInfo?.id,
          userId: appContext.ext?.userId,
        });
        // appContext?.utils?.$track?.({
        //   event: val,
        //   params: {
        //     dialogId: appContext?.dialogInfo?.id,
        //     userId: appContext.ext?.userId,
        //   },
        // });
      } catch (error) {}
    }
  }, []);
  const onLongPress = () => {
    $track('onLongPress');
    setShowOption(true);
  };
  const handleMessageTouchStart = useCallback(
    (e: TouchEvent | MouseEvent) => {
      if (
        !(
          appContext?.renderConfig?.messageDelete ||
          (appContext?.ext?.userDefaultConfig?.openReply &&
            !appContext?.renderConfig?.hideReply)
        )
      ) {
        return;
      }
      // 兼容最下面消息的气泡展示
      const isLastMessage =
        appContext?.messages?.slice(-1)?.[0]?.clientId === message.clientId;
      setPopDirection(isLastMessage);
      // 兼容处理在没有头像的时候，发送文字果断气泡超出展示的 case
      if (
        appContext?.renderConfig?.messageDelete &&
        appContext?.renderConfig?.hideAvatar &&
        appContext?.ext?.userDefaultConfig?.openReply &&
        !appContext?.renderConfig?.hideReply
      ) {
        const noAvatarRightGap = 120;
        const { width: windowWidth } = document.body.getBoundingClientRect();
        const { x, width: contentWidth } =
          e?.currentTarget?.getBoundingClientRect() || {};
        const isBorderCase =
          (message.isFromMe && windowWidth - x < noAvatarRightGap) ||
          (!message.isFromMe && contentWidth + x < noAvatarRightGap);
        if (isBorderCase) {
          setNoAvatarCls(
            message.isFromMe ? 'deleteBtnNoAvatar' : 'deleteBtnNoAvatarAgrent',
          );
        }
      }
      if (!appContext.query?.isPc) {
        timeout.current = window?.setTimeout(() => {
          onLongPress();
        }, delay);
      }
    },
    [onLongPress, delay],
  );
  const handleMessageTouchEnd = useCallback(() => {
    // 删除开关和回复开关都没开的话，删除开关跟业务线走，回复开关在tcc配置
    if (
      !(
        appContext?.renderConfig?.messageDelete ||
        (appContext?.ext?.userDefaultConfig?.openReply &&
          !appContext?.renderConfig?.hideReply)
      )
    ) {
      return;
    }
    if (!appContext.query?.isPc) {
      timeout.current && window.clearTimeout(timeout.current);
      timeout.current = null;
    }
  }, []);
  // Long press event - end
  const handleBtnClick = useCallback(
    (event) => {
      event.stopPropagation();
      Dialog.confirm({
        title: I18n.t('message_delete_title', {}, '删除确认'),
        content: I18n.t('message_delete_content', {}, '是否删除该条消息？'),
        // content: message.serverId,
        confirmText: I18n.t('close_dialog_confirm_text', {}, '确认'),
        cancelText: I18n.t('close_dialog_cancel_text', {}, '取消'),
        onConfirm: () => {
          appContext?.utils?.services?.apis?.modifyMessage?.({
            dialogId: appContext.dialog?.ext?.dialogId,
            serverId: message.serverId,
            extMap: {
              's:invisible': appContext.dialog?.customerId,
            },
          });
        },
      });
      $track('messageDelete');
    },
    [message],
  );
  // 回复
  const handleReply = useCallback(
    (event: React.TouchEvent | React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setShowReplyTip(false);
      setPcReplyColor(NORMAL_COLOR);
      if (message.serverId) {
        let { senderType, type, recalled, content, serverId } = message;
        appContext.replyMessage = {
          type,
          senderType,
          content,
          serverId,
          recalled,
        };
        appContext?.$refs?.$input?.current?.focus();
        $track('messageReply');
      }
      setShowOption(false);
    },
    [setShowOption, message],
  );
  useEffect(() => {
    if (!showOption) return;
    let hideOption = (e) => {
      if (
        e?.target?.className &&
        e?.target?.className?.indexOf?.('viewer') > -1
      ) {
        return;
      }
      setShowOption(false);
    };
    document.addEventListener('touchstart', hideOption);
    document.addEventListener('mousedown', hideOption);
  }, [showOption]);
  const {
    component = {} as IComponent,
    content,
    type,
    flightStatus,
    ext = {},
  } = message;

  const NativeDiv = ({ children, ...props }) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      ref.current!.addEventListener('touchstart', handleReply, false);
      ref.current!.addEventListener('mousedown', handleReply, false);
      return () => {
        ref.current!.removeEventListener('touchstart', handleReply, false);
        ref.current!.removeEventListener('mousedown', handleReply, false);
      };
    }, []);
    return (
      <div {...props} ref={ref}>
        {children}
      </div>
    );
  };

  const MessageContent = useMemo(() => {
    // 消息撤回
    if (message.isRecalled) {
      return <MessageRecall appContext={appContext} message={message} />;
    }
    return (
      <div
        className="messageContent"
        onMouseOver={handleMessageHover}
        onMouseLeave={handleMessageLeave}
        onMouseDown={handleMessageTouchStart}
        onMouseUp={handleMessageTouchEnd}
        onTouchStart={handleMessageTouchStart}
        onTouchEnd={handleMessageTouchEnd}
        data-rootMessageId={message.ext.rootMessageId}
        data-serverId={message.serverId} // 机器人数据细分参数
      >
        {component ? (
          <LazyComponent config={component} message={message} />
        ) : null}
        {showOption &&
          !Object.values(MESSAGE_SYSTEM_TYPE).includes(message.type) &&
          message?.user?.avatar &&
          appContext.query?.isPc && (
            <div className={`opBtnPc ${message.isFromMe ? 'opBtnPcMe' : ''}`}>
              {/* 回复 */}
              {!appContext?.renderConfig?.hideReply &&
                appContext?.ext?.userDefaultConfig?.openReply && (
                  <div
                    className="deleteBtnPc"
                    onMouseOver={() => {
                      handleBtnHover('reply');
                    }}
                    onMouseLeave={handleBtnLeave}
                    onMouseDown={handleReply}
                  >
                    <ReplyIcon fill={pcReplyColor}></ReplyIcon>
                    {showReplyTip && (
                      <div className={`tipWrap tipWrapUp`}>
                        <div>{I18n.t('reply', {}, '回复')}</div>
                        <div className="arrowDown arrowDownPc"></div>
                      </div>
                    )}
                  </div>
                )}
              {/* 删除 */}
              {appContext?.renderConfig?.messageDelete && (
                <div
                  className="deleteBtnPc"
                  onMouseOver={() => {
                    handleBtnHover('delete');
                  }}
                  onMouseLeave={handleBtnLeave}
                  onMouseDown={handleBtnClick}
                >
                  <DeleteIcon fill={pcDeleteColor}></DeleteIcon>
                  {showDeleteTip && (
                    <div className={`tipWrap tipWrapUp`}>
                      <div>{I18n.t('delete_tip', {}, '删除')}</div>
                      <div className="arrowDown arrowDownPc"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        {showOption &&
          !appContext?.query?.isPc &&
          !Object.values(MESSAGE_SYSTEM_TYPE).includes(message.type) && (
            <>
              <div className={`${popDirection ? 'arrowDown' : 'arrow'}`}></div>
              <div
                className={`deleteBtn ${
                  popDirection ? 'deleteBtnUp' : ''
                } ${noAvatarCls}`}
              >
                {!appContext?.renderConfig?.hideReply &&
                  appContext?.ext?.userDefaultConfig?.openReply && (
                    <NativeDiv className="op-btn">
                      <ReplyIcon></ReplyIcon>
                      <div className="text">{I18n.t('reply', {}, '回复')}</div>
                    </NativeDiv>
                  )}
                {appContext?.renderConfig?.messageDelete && (
                  <div
                    className="op-btn"
                    onTouchStart={handleBtnClick}
                    onMouseDown={handleBtnClick}
                  >
                    <DeleteIcon></DeleteIcon>
                    <div className="text">
                      {I18n.t('delete_tip', {}, '删除')}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
      </div>
    );
  }, [
    JSON.stringify(component),
    content,
    type,
    flightStatus,
    JSON.stringify(ext),
    showOption,
    pcDeleteColor,
    pcReplyColor,
  ]);

  return (
    <div
      className={prefixCls({
        [`${classes.prefix}MessageContainer`]: true,
        typeSetRight: message.position === 'right',
      })}
      style={{
        justifyContent: message.position === 'center' ? 'center' : 'flex-start',
        ...style,
      }}
      data-kid={message._id}
      data-sid={message.indexInConversation?.toString()}
    >
      {MessageAvatar}
      {MessageContent}
      {message.expansion && message.expansion.length ? (
        <div
          className={prefixCls({
            extDiv: true,
            typeSetRight: message.position === 'right',
          })}
        >
          {message.expansion &&
            message.expansion.map((item) => (
              <LazyComponent
                config={item}
                message={message}
                data-react-key={item.code}
                key={item.code}
              />
            ))}
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(MessageContainer, (prevProps, nextProps) => {
  const { message: prev, avatarsConfig: prevConfig } = prevProps;
  const { message: next, avatarsConfig: nextConfig } = nextProps;
  try {
    return nextConfig === prevConfig && isEqual(prev, next);
  } catch (error) {
    return false;
  }
});
